'use client'

import {
  createContext,
  PropsWithChildren,
  ReactNode,
  RefObject,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
} from 'react'

export const useScroll = <T extends HTMLElement>() => {
  const ref = useRef<T>(null)

  const scroll = useCallback(
    ({ behavior = 'instant', top = 0 }: ScrollToOptions = {}) => {
      ref.current?.scroll({ top, behavior })
    },
    [],
  )

  const scrollIntoView = useCallback(
    ({
      behavior = 'instant',
      block = 'center',
      ...restOptions
    }: ScrollIntoViewOptions = {}) => {
      ref.current?.scrollIntoView({ behavior, block, ...restOptions })
    },
    [],
  )

  return { ref, scroll, scrollIntoView }
}

type ScrollState<T extends HTMLElement> = {
  ref: RefObject<T | null>
  scroll: (options?: ScrollToOptions) => void
  scrollIntoView: (options?: ScrollIntoViewOptions) => void
}

export const ScrollContext = createContext<ScrollState<HTMLElement> | null>(
  null,
)

export const ScrollProvider = ({ children }: PropsWithChildren) => {
  const { ref, scrollIntoView, scroll } = useScroll<HTMLDivElement>()

  const value = useMemo(
    () => ({ ref, scrollIntoView, scroll }),
    [ref, scrollIntoView, scroll],
  )

  return (
    <ScrollContext.Provider value={value}>{children}</ScrollContext.Provider>
  )
}

export const useScrollState = <T extends HTMLElement>() => {
  const context = useContext(ScrollContext)

  if (!context) {
    throw new Error('useScrollState must be used within a ScrollProvider')
  }

  return context as unknown as ScrollState<T>
}

export const useMultiScroll = () => {
  const ref = useRef<{
    [key: string]: HTMLElement | null
  }>({})

  const registerScrollElement = useCallback(
    (key: string, element: HTMLElement) => {
      ref.current[key] = element
    },
    [],
  )

  const unregisterScrollElement = useCallback((key: string) => {
    // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
    delete ref.current[key]
  }, [])

  const scroll = useCallback(
    (
      key: string,
      {
        top: topProp,
        behavior = 'instant',
        offset = 0,
      }: ScrollToOptions & { offset?: number } = {},
    ) => {
      if (topProp) {
        window.scrollTo({ top: topProp + offset, behavior })
      } else {
        const element = ref.current[key]
        const top = element?.getBoundingClientRect().top || 0
        window.scrollTo({ top: top + window.scrollY + offset, behavior })
      }
    },
    [],
  )

  const scrollIntoView = useCallback(
    (
      key: string,
      {
        behavior = 'instant',
        block = 'center',
        ...restOptions
      }: ScrollIntoViewOptions = {},
    ) => {
      const element = ref.current[key]
      element?.scrollIntoView({ behavior, block, ...restOptions })
    },
    [],
  )

  const getElement = useCallback((key: string) => {
    return ref.current[key]
  }, [])

  return {
    ref,
    registerScrollElement,
    unregisterScrollElement,
    scroll,
    scrollIntoView,
    getElement,
  }
}

type MultiScrollState<T extends HTMLElement> = {
  target?: string
  ref: RefObject<{
    [key: string]: T | null
  }>
  registerScrollElement: (key: string, element: T) => void
  unregisterScrollElement: (key: string) => void
  scroll: (key: string, options?: ScrollToOptions & { offset?: number }) => void
  scrollIntoView: (key: string, options?: ScrollIntoViewOptions) => void
  getElement: (key: string) => T | null
}

export const MultiScrollContext =
  createContext<MultiScrollState<HTMLElement> | null>(null)

type MultiScrollProviderProps = {
  target?: string
  children: ReactNode
}

export const MultiScrollProvider = ({
  target,
  children,
}: MultiScrollProviderProps) => {
  const {
    ref,
    scrollIntoView,
    scroll,
    registerScrollElement,
    unregisterScrollElement,
    getElement,
  } = useMultiScroll()

  const value = useMemo(
    () => ({
      target,
      ref,
      scrollIntoView,
      scroll,
      registerScrollElement,
      unregisterScrollElement,
      getElement,
    }),
    [
      target,
      ref,
      scrollIntoView,
      scroll,
      registerScrollElement,
      unregisterScrollElement,
      getElement,
    ],
  )

  useEffect(() => {
    if (target) {
      requestAnimationFrame(() => {
        scrollIntoView(target, { behavior: 'smooth', block: 'start' })
      })
    }
  }, [target, scrollIntoView])

  return (
    <MultiScrollContext.Provider value={value}>
      {children}
    </MultiScrollContext.Provider>
  )
}

export const useMultiScrollState = <T extends HTMLElement>() => {
  const context = useContext(MultiScrollContext)

  if (!context) {
    throw new Error(
      'useMultiScrollState must be used within a MultiScrollProvider',
    )
  }

  return context as unknown as MultiScrollState<T>
}
